import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, FileText, CheckCircle, AlertCircle, 
  Loader2, FileSpreadsheet, Calendar, User, History, Download, Eye, X
} from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDate } from '../../utils/dateUtils';

const HistoricalPayrollImport = ({ preselectedMonth, preselectedYear, onImportComplete }) => {
  const [selectedMonth, setSelectedMonth] = useState(preselectedMonth || new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(preselectedYear || new Date().getFullYear());
  const [excelFile, setExcelFile] = useState(null);
  const [notes, setNotes] = useState('');
  
  // Action States
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadingBatchId, setDownloadingBatchId] = useState(null);
  
  // Data States
  const [importHistory, setImportHistory] = useState([]);
  const [validationResult, setValidationResult] = useState(null);
  const { showSuccess, showError } = useNotification();
  const [success, setSuccessState] = useState('');
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => {
    setSuccessState(msg);
    if (msg) showSuccess(msg);
  };
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [creatingPayroll, setCreatingPayroll] = useState(false);
  const [payrollCreated, setPayrollCreated] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImportHistory();
  }, []);

  // Sync with props if they change
  useEffect(() => {
    if (preselectedMonth) setSelectedMonth(preselectedMonth);
    if (preselectedYear) setSelectedYear(preselectedYear);
  }, [preselectedMonth, preselectedYear]);

  const fetchImportHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(buildApiUrl('/api/v1/historical-payroll/batches'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.imports || []);
      }
    } catch (err) {
      console.error('Error fetching import history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExcelFile(file);
      setValidationResult(null);
      setError('');
      setSuccess('');
    }
  };

  const handleValidateExcel = async () => {
    if (!excelFile) {
      setError('Please select an Excel file to validate.');
      return;
    }

    setValidating(true);
    setError('');
    setSuccess('');
    setValidationResult(null);

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('payroll_month', selectedMonth);
      formData.append('payroll_year', selectedYear);

      const response = await fetch(buildApiUrl('/api/v1/historical-payroll/preview-upload'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        setValidationResult(data);
        if (data.employee_validation?.ready_to_import && data.head_validation?.ready_to_import) {
          setSuccess('Excel validation passed! Excel data matches Master Employee records.');
        } else {
          let errorMsg = 'Validation issues found: ';
          if (data.employee_validation?.manual_mapping_required > 0) {
            errorMsg += `${data.employee_validation.manual_mapping_required} employees require mapping. `;
          }
          if (data.employee_validation?.missing_from_master > 0) {
            errorMsg += `${data.employee_validation.missing_from_master} employees missing in Master database. `;
          }
          if (data.head_validation?.manual_mapping_required > 0) {
            errorMsg += `${data.head_validation.manual_mapping_required} payroll heads need mapping. `;
          }
          setError(errorMsg);
        }
      } else {
        setError(data.detail || 'Excel validation failed.');
      }
    } catch (err) {
      setError('Network error during validation. Please check file structure and try again.');
    } finally {
      setValidating(false);
    }
  };

  const handleImportExcel = async () => {
    if (!excelFile) return;

    setImporting(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('payroll_month', selectedMonth);
      formData.append('payroll_year', selectedYear);
      if (notes) formData.append('notes', notes);

      const response = await fetch(buildApiUrl('/api/v1/historical-payroll/upload'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Successfully imported historical payroll run for ${getMonthName(selectedMonth)} ${selectedYear}.`);
        setExcelFile(null);
        setNotes('');
        setValidationResult(null);
        setPayrollCreated(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        
        await fetchImportHistory();
        if (onImportComplete) {
          onImportComplete();
        }
      } else {
        // Handle nested detail structure if present
        if (data.detail && typeof data.detail === 'object') {
          setError(data.detail.message || 'Import failed.');
        } else {
          setError(data.detail || 'Import failed.');
        }
      }
    } catch (err) {
      setError('Network error during import.');
    } finally {
      setImporting(false);
    }
  };

  const handleCreatePayrollFromImport = async () => {
    if (!validationResult || !validationResult.batch_id) {
      setError('No valid import batch found. Please upload and validate an Excel file first.');
      return;
    }

    setCreatingPayroll(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('batch_id', validationResult.batch_id);

      const response = await fetch(buildApiUrl('/api/v1/historical-payroll/import-to-payroll'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(`Payroll created successfully! ${data.salary_slips_created} salary slips created. Opening payroll editor...`);
        setPayrollCreated(true);
        
        // Refresh import history
        await fetchImportHistory();
        
        // Notify parent component to open payroll editor
        if (onImportComplete) {
          onImportComplete();
        }
        
        // Dispatch event to open payroll editor
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('open-payroll-editor', {
            detail: { payroll_run_id: data.payroll_run_id }
          }));
        }, 1500);
      } else {
        setError(data.detail || 'Failed to create payroll from import.');
      }
    } catch (err) {
      setError('Network error during payroll creation.');
    } finally {
      setCreatingPayroll(false);
    }
  };

  const handleDownloadBatch = async (batchId, filename) => {
    setDownloadingBatchId(batchId);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/historical-payroll/batches/${batchId}/download`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || 'original_payroll.xlsx');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
      } else {
        alert('Failed to download batch file');
      }
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  const getMonthName = (m) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[m - 1];
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* LEFT: Import Wizard Card */}
      <div className="lg:col-span-2 space-y-6">
        <section className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold text-white flex items-center">
              <Upload className="w-5 h-5 mr-2" />
              Import Historical Payroll Excel
            </h2>
            <span className="bg-blue-800 text-white text-xs px-2.5 py-1 rounded-full font-semibold">MIGRATION ONLY</span>
          </div>

          <div className="p-6 space-y-6">
            {success && !payrollCreated && validationResult && validationResult.batch_id && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-800 font-medium mb-1">Payroll data imported successfully.</p>
                    <p className="text-xs text-blue-700">Create payroll records to complete the import process.</p>
                  </div>
                  <button
                    onClick={handleCreatePayrollFromImport}
                    disabled={creatingPayroll}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center text-sm font-semibold"
                  >
                    {creatingPayroll ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating Payroll...
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Create Payroll from Import
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Target Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(parseInt(e.target.value));
                    setValidationResult(null);
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700"
                >
                  {Array.from({ length: Math.max(1, new Date().getFullYear() - 2025 + 1) }, (_, i) => 2025 + i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Target Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(parseInt(e.target.value));
                    setValidationResult(null);
                  }}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700"
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{getMonthName(i + 1)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Select Payroll Excel Document</label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xlsx, .xls"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-5 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center font-medium text-gray-700"
                >
                  <FileSpreadsheet className="w-5 h-5 text-gray-500 mr-2" />
                  {excelFile ? 'Change File' : 'Choose File'}
                </button>
                {excelFile && (
                  <div className="text-sm font-medium text-gray-600 truncate max-w-[250px]">
                    {excelFile.name}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Migration Notes / Remarks</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter baseline details, e.g., 'Legacy HRMS final run backup'"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-700"
                rows={2}
              />
            </div>

            {/* Validation Feedback Details */}
            {validationResult && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                <h4 className="font-bold text-gray-800 flex items-center text-sm border-b pb-2">
                  <Eye className="w-4 h-4 text-blue-600 mr-1.5" />
                  Excel Pre-Validation Snapshot
                </h4>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs font-semibold text-gray-600">
                  <div>Header Row Detected: <span className="text-gray-900 font-bold">{(validationResult.header_row || 0) + 1}</span></div>
                  <div>Detected Period: <span className="text-gray-900 font-bold">{validationResult.detected_month}/{validationResult.detected_year}</span></div>
                  <div>Matched Employees: <span className="text-green-600 font-bold">{validationResult.employee_validation?.already_matched}</span></div>
                  <div>Need Manual Mapping: <span className="text-yellow-600 font-bold">{validationResult.employee_validation?.manual_mapping_required}</span></div>
                  <div>Missing in Master DB: <span className="text-red-600 font-bold">{validationResult.employee_validation?.missing_from_master}</span></div>
                  <div>Mapped Payroll Heads: <span className="text-green-600 font-bold">{validationResult.head_validation?.already_matched + validationResult.head_validation?.legacy_mapped}</span></div>
                </div>

                <div className="pt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowPreviewModal(true)}
                    className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    View Column Mapping & Preview data
                  </button>
                </div>
              </div>
            )}

            <div className="pt-2 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={handleValidateExcel}
                disabled={!excelFile || validating || importing}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold flex items-center text-sm"
              >
                {validating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Validating Excel...
                  </>
                ) : (
                  'Validate Excel'
                )}
              </button>

              <button
                type="button"
                onClick={handleImportExcel}
                disabled={!validationResult || !validationResult.employee_validation?.ready_to_import || !validationResult.head_validation?.ready_to_import || importing}
                className="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-semibold flex items-center text-sm"
              >
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing Baseline...
                  </>
                ) : (
                  'Import Payroll'
                )}
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* RIGHT: History Card */}
      <div className="lg:col-span-1">
        <section className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden h-full flex flex-col">
          <div className="bg-gradient-to-r from-gray-700 to-gray-800 px-6 py-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <History className="w-5 h-5 mr-2" />
              Migration Log
            </h2>
          </div>

          <div className="p-4 flex-1 overflow-y-auto space-y-4 max-h-[500px]">
            {loadingHistory ? (
              <div className="flex flex-col items-center justify-center py-10 space-y-2">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <span className="text-sm font-medium text-gray-500">Loading import logs...</span>
              </div>
            ) : importHistory.length === 0 ? (
              <div className="text-center py-10">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No historical payroll runs imported yet.</p>
              </div>
            ) : (
              importHistory.map((item) => (
                <div key={item.id} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2 relative hover:bg-gray-100 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-gray-900 text-sm">
                        {getMonthName(item.payroll_month)} {item.payroll_year}
                      </h4>
                      <p className="text-xs font-semibold text-gray-500 mt-0.5 truncate max-w-[150px]">
                        {item.original_filename}
                      </p>
                    </div>
                    <span className="bg-green-100 text-green-800 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">
                      IMPORTED
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-1 text-[11px] font-semibold text-gray-600 pt-1 border-t">
                    <div>Size: <span className="text-gray-900 font-bold">{item.total_records} emps</span></div>
                    <div className="text-right">By: <span className="text-gray-900 font-bold">{item.created_by_name || 'Admin'}</span></div>
                  </div>

                  <div className="pt-2 flex justify-between items-center text-xs">
                    <span className="text-gray-400 text-[10px] font-medium">
                      {formatDate(item.upload_date)}
                    </span>
                    
                    <button
                      onClick={() => handleDownloadBatch(item.id, item.original_filename)}
                      disabled={downloadingBatchId === item.id}
                      className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-0.5"
                    >
                      {downloadingBatchId === item.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      Source file
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      {/* PREVIEW MODAL */}
      {showPreviewModal && validationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Payroll File Mapping & Preview</h2>
                <p className="text-blue-100 text-sm">
                  {getMonthName(selectedMonth)} {selectedYear}
                </p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Columns Detected */}
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center">
                  <FileSpreadsheet className="w-4.5 h-4.5 text-blue-600 mr-1.5" />
                  Mapped Excel Columns
                </h3>
                <div className="flex flex-wrap gap-2">
                  {validationResult.preview?.mapped_columns?.map((col) => (
                    <span key={col} className="bg-green-50 text-green-700 border border-green-200 text-xs px-2.5 py-1 rounded-md font-semibold">
                      {col}
                    </span>
                  ))}
                  {validationResult.preview?.unknown_columns?.map((col) => (
                    <span key={col} className="bg-gray-100 text-gray-600 border border-gray-200 text-xs px-2.5 py-1 rounded-md font-semibold">
                      {col} (Ignored)
                    </span>
                  ))}
                </div>
              </div>

              {/* Data Preview Table */}
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-3 flex items-center">
                  <Eye className="w-4.5 h-4.5 text-blue-600 mr-1.5" />
                  First 5 Data Rows
                </h3>
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 font-bold text-gray-700">
                        <th className="p-3">Emp Code</th>
                        <th className="p-3">Name</th>
                        <th className="p-3">Designation</th>
                        <th className="p-3">Basic</th>
                        <th className="p-3">Gross Pay</th>
                        <th className="p-3">Total Deductions</th>
                        <th className="p-3">Net Pay</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 font-medium text-gray-600">
                      {validationResult.preview?.first_five_records?.map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-3 font-semibold text-gray-900">{row.employee_code}</td>
                          <td className="p-3 text-gray-900">{row.employee_name}</td>
                          <td className="p-3">{row.designation || row.grade || 'N/A'}</td>
                          <td className="p-3">₹{(row.emp_basic || row.basic || 0).toLocaleString()}</td>
                          <td className="p-3">₹{(row.gross_pay || row.total_earning || 0).toLocaleString()}</td>
                          <td className="p-3">₹{(row.total_deductions || 0).toLocaleString()}</td>
                          <td className="p-3 text-green-700 font-bold">₹{(row.pay_to_ac || row.net_pay || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm font-semibold text-gray-700"
              >
                Close Preview
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default HistoricalPayrollImport;
